import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { backendService } from "@/services/backend.service";

/** The network field is free text, so the list waits until typing pauses rather than refetching per key. */
const NETWORK_DEBOUNCE_MS = 400;
/** A network's facilities change only when a department is created, so a minute-old list is fine. */
const FACILITIES_STALE_MS = 60_000;

/** Every network's facility list. Invalidated after a department is created, which may have made a new facility. */
export const HOSPITAL_FACILITIES_QUERY_KEY = ["hospital-facilities"] as const;

export const hospitalFacilitiesQueryKey = (network: string) => [...HOSPITAL_FACILITIES_QUERY_KEY, network] as const;

/**
 * The facilities of the network typed on the Create Department form, for its facility picker.
 *
 * `enabled` carries the caller's support check: the endpoint is support-only, so anyone else would
 * fire a guaranteed 403. `retry` is off because the picker shows a failure inline with its own retry
 * button, and "New facility" stays available whatever the list does.
 *
 * `network` is the trimmed network the list is for; empty until the field has settled on something.
 * `isSettling` is true while the typed network hasn't reached `network` yet, when the list on hand
 * still belongs to the previous one.
 */
export function useHospitalFacilities({ hospital, enabled }: { hospital: string; enabled: boolean }) {
	const { track, trackError } = useAnalytics();
	const network = useDebouncedValue(hospital, NETWORK_DEBOUNCE_MS).trim();

	const query = useQuery({
		queryKey: hospitalFacilitiesQueryKey(network),
		queryFn: async () => (await backendService.getHospitalFacilities(network)).data,
		enabled: enabled && network !== "",
		retry: false,
		staleTime: FACILITIES_STALE_MS,
	});

	useEffect(() => {
		if (query.error) trackError("dept_config_facilities_fetch_error", { network, error_message: query.error.message });
	}, [query.error, network, trackError]);

	/** The picker's Retry: a user action, so it gets its own start/success/error events. */
	const retryFetch = async () => {
		track("dept_config_facilities_retry_start", { network });
		const result = await query.refetch();
		if (result.isSuccess) track("dept_config_facilities_retry_success", { network, facility_count: result.data.length });
		if (result.isError) trackError("dept_config_facilities_retry_error", { network, error_message: result.error.message });
	};

	return { ...query, retryFetch, network, isSettling: hospital.trim() !== network };
}
